import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import io from 'socket.io-client';

// Create the context
const GameContext = createContext();

// Custom hook to use the game context
export const useGame = () => useContext(GameContext);

// Provider component
export const GameProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [game, setGame] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Game-specific state
  const [dictionary, setDictionary] = useState([]);
  const [assignedWords, setAssignedWords] = useState([]);
  const [wordInputs, setWordInputs] = useState({});
  const [dictionaryLanguage, setDictionaryLanguage] = useState('pt-br');
  const [wordVisibility, setWordVisibility] = useState({});
  
  // Turn-based gameplay state
  const [currentEra, setCurrentEra] = useState(0);
  const [activePlayer, setActivePlayer] = useState(null);
  const [activePlayerName, setActivePlayerName] = useState('');
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [gameState, setGameState] = useState('waiting');
  const [assignedWord, setAssignedWord] = useState('');
  const [wordDescription, setWordDescription] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [guessInput, setGuessInput] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [readyForNextEra, setReadyForNextEra] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [currentWord, setCurrentWord] = useState('');

  // Helper function to ensure player names are strings
  const ensureStringName = (name) => {
    return typeof name === 'string' ? name : String(name || '');
  };

  // Setup game event handlers
  const setupGameEventHandlers = useCallback((socket) => {
    if (!socket) return;
    
    socket.on('gameCreated', (data) => {
      console.log('Game created event received:', data);
      
      // Check if we have valid data
      if (!data) {
        console.error('Received empty data in gameCreated event');
        return;
      }
      
      // Handle different data formats - the server might send data in different formats
      let gameData;
      
      // Case 1: data has gameId at the top level (not nested in a game object)
      if (data.gameId) {
        console.log('Data has gameId at top level, constructing game object');
        gameData = {
          id: data.gameId,
          players: data.players || [],
          state: data.state || 'waiting',
          currentEra: data.currentEra || 0
        };
      } 
      // Case 2: data has a game property containing the game object
      else if (data.game && data.game.id) {
        console.log('Data has nested game object');
        gameData = data.game;
      }
      // Case 3: data is the game object itself with an id property
      else if (data.id) {
        console.log('Data is the game object itself');
        gameData = data;
      }
      else {
        console.error('Invalid game data format received:', data);
        return;
      }
      
      console.log('Setting game with ID:', gameData.id);
      
      // Sanitize player names to ensure they are strings
      if (gameData.players) {
        gameData.players = gameData.players.map(player => ({
          ...player,
          name: ensureStringName(player.name)
        }));
      }
      
      // Set the game object
      setGame(gameData);
      
      // Set game state
      setGameState(gameData.state || 'waiting');
      
      // Clear any error messages
      setError('');
    });

    socket.on('gameJoined', (data) => {
      console.log('Game joined event received:', data);
      
      // Check if we have valid data
      if (!data) {
        console.error('Received empty data in gameJoined event');
        return;
      }
      
      // Handle different data formats - the server might send data in different formats
      let gameData;
      
      // Case 1: data has gameId at the top level (not nested in a game object)
      if (data.gameId) {
        console.log('Data has gameId at top level, constructing game object');
        gameData = {
          id: data.gameId,
          players: data.players || [],
          state: data.state || 'waiting',
          currentEra: data.currentEra || 0
        };
      } 
      // Case 2: data has a game property containing the game object
      else if (data.game && data.game.id) {
        console.log('Data has nested game object');
        gameData = data.game;
      }
      // Case 3: data is the game object itself with an id property
      else if (data.id) {
        console.log('Data is the game object itself');
        gameData = data;
      }
      else {
        console.error('Invalid game data format received:', data);
        return;
      }
      
      console.log('Setting game with ID:', gameData.id);
      
      // Sanitize player names to ensure they are strings
      if (gameData.players) {
        gameData.players = gameData.players.map(player => ({
          ...player,
          name: ensureStringName(player.name)
        }));
      }
      
      // Set the game object
      setGame(gameData);
      
      // Set active player if available
      if (gameData.activePlayer) {
        setActivePlayer(gameData.activePlayer);
        
        // Find the active player in the players array and get their name
        const activePlayerObj = gameData.players?.find(p => p.id === gameData.activePlayer);
        if (activePlayerObj) {
          setActivePlayerName(ensureStringName(activePlayerObj.name));
        }
      }
      
      // Set game state
      setGameState(gameData.state || 'waiting');
      
      // Set current era
      if (gameData.board && typeof gameData.board.currentEra === 'number') {
        setCurrentEra(gameData.board.currentEra);
      } else if (typeof gameData.currentEra === 'number') {
        setCurrentEra(gameData.currentEra);
      }
      
      // Set assigned words if available
      if (data.assignedWords && Array.isArray(data.assignedWords)) {
        setAssignedWords(data.assignedWords);
      }
      
      // Set dictionary if available
      if (data.dictionary && Array.isArray(data.dictionary)) {
        setDictionary(data.dictionary);
      }
      
      // Clear any error messages
      setError('');
    });

    socket.on('playerJoined', (updatedGame) => {
      console.log('Player joined:', updatedGame);
      
      // Ensure player names are strings
      if (updatedGame && updatedGame.players) {
        const sanitizedPlayers = updatedGame.players.map(player => ({
          ...player,
          name: ensureStringName(player.name)
        }));
        
        setGame(prevGame => ({
          ...prevGame,
          players: sanitizedPlayers,
          state: updatedGame.state || prevGame?.state || 'waiting'
        }));
        
        if (updatedGame.state) {
          setGameState(updatedGame.state);
        }
      }
    });
    
    socket.on('playerLeft', (updatedGame) => {
      console.log('Player left:', updatedGame);
      
      // Ensure player names are strings
      if (updatedGame && updatedGame.players) {
        const sanitizedPlayers = updatedGame.players.map(player => ({
          ...player,
          name: ensureStringName(player.name)
        }));
        
        setGame(prevGame => ({
          ...prevGame,
          players: sanitizedPlayers,
          state: updatedGame.state || prevGame?.state || 'waiting'
        }));
        
        if (updatedGame.state) {
          setGameState(updatedGame.state);
        }
      }
    });
    
    socket.on('gameStarted', (data) => {
      console.log('Game started:', data);
      
      if (data) {
        // Update game state
        if (data.state) {
          console.log('Game state updated to:', data.state);
          setGameState(data.state);
        }
        
        // Update player list
        if (data.players) {
          // Ensure all player names are strings
          const sanitizedPlayers = data.players.map(player => ({
            ...player,
            name: ensureStringName(player.name)
          }));
          
          // Update game with new players
          setGame(prevGame => ({
            ...prevGame,
            players: sanitizedPlayers,
            state: data.state || prevGame?.state || 'waiting'
          }));
        }
        
        // Update current era
        if (data.currentEra !== undefined) {
          setCurrentEra(data.currentEra);
        }
      }
    });

    socket.on('dictionaryUpdated', (data) => {
      console.log('Dictionary updated:', data);
      if (data && data.dictionary) {
        setDictionary(data.dictionary);
        
        // Update game state if provided
        if (data.gameState) {
          setGameState(data.gameState);
        }
        
        // Update players if provided
        if (data.players) {
          setGame(prevGame => ({
            ...prevGame,
            players: data.players
          }));
        }
      }
    });

    socket.on('wordsAssigned', (data) => {
      console.log('Words assigned:', data);
      if (data && data.assignedWords && Array.isArray(data.assignedWords)) {
        console.log('Setting assigned words:', data.assignedWords);
        setAssignedWords(data.assignedWords);
        
        // Clear any existing word inputs for these words
        const newWordInputs = {};
        data.assignedWords.forEach(word => {
          newWordInputs[word] = '';
        });
        setWordInputs(newWordInputs);
      }
    });

    socket.on('eraComplete', (data) => {
      console.log('Era complete event received:', data);
      if (data) {
        setGameState('era_complete');
        if (data.era !== undefined) {
          setCurrentEra(data.era);
        }
      }
    });
    
    socket.on('playerReadyUpdated', (data) => {
      console.log('Player ready updated:', data);
      if (data && data.players) {
        setGame(prevGame => prevGame ? {
          ...prevGame,
          players: data.players
        } : null);
      }
    });
    
    socket.on('newEraStarted', (data) => {
      console.log('New era started:', data);
      if (data) {
        if (data.era !== undefined) {
          setCurrentEra(data.era);
        }
        
        if (data.activePlayer) {
          setActivePlayer(data.activePlayer);
          setIsMyTurn(data.activePlayer === socket.id);
        }
        
        if (data.activePlayerName !== undefined) {
          setActivePlayerName(ensureStringName(data.activePlayerName));
        }
        
        if (data.state) {
          setGameState(data.state);
        }
      }
    });
    
    socket.on('turnChanged', (data) => {
      console.log('Turn changed:', data);
      if (data) {
        if (data.activePlayer) {
          setActivePlayer(data.activePlayer);
          setIsMyTurn(data.activePlayer === socket.id);
        }
        
        // Make sure activePlayerName is a string
        if (data.activePlayerName !== undefined) {
          setActivePlayerName(ensureStringName(data.activePlayerName));
        }
        
        if (data.state) {
          setGameState(data.state);
          
          // Reset current word when turn changes to turn_setup
          if (data.state === 'turn_setup') {
            setCurrentWord('');
          }
        }
      }
    });
    
    socket.on('wordAssigned', (data) => {
      console.log('Word assigned:', data);
      if (data && data.word) {
        setAssignedWord(data.word);
        setCurrentWord(data.word);
      }
    });
    
    socket.on('turnStarted', (data) => {
      console.log('Turn started:', data);
      if (data) {
        // Ensure active player name is a string
        if (data.activePlayer) {
          setActivePlayer(data.activePlayer);
          setIsMyTurn(data.activePlayer === socket.id);
          
          if (data.activePlayerName !== undefined) {
            setActivePlayerName(ensureStringName(data.activePlayerName));
          }
        }
        
        if (data.state) {
          setGameState(data.state);
        }
      }
    });
    
    socket.on('descriptionSubmitted', (data) => {
      console.log('Description submitted:', data);
      if (data) {
        if (data.description) {
          setWordDescription(data.description);
        }
        
        if (data.state) {
          setGameState(data.state);
        }
      }
    });
    
    socket.on('guessSubmitted', (data) => {
      console.log('Guess submitted:', data);
      if (data) {
        // Ensure playerName is a string
        if (data.playerName && typeof data.playerName !== 'string') {
          console.warn('Received non-string playerName:', data.playerName);
          data.playerName = String(data.playerName || '');
        }
        
        setGuesses(prev => [...prev, data]);
      }
    });
    
    socket.on('correctGuess', (data) => {
      console.log('Correct guess:', data);
      if (data) {
        if (data.state) {
          setGameState(data.state);
        }
        
        // If I'm the active player, set the current word for spelling
        if (data.word && activePlayer === socket.id) {
          setCurrentWord(data.word);
        }
      }
    });
    
    socket.on('gameCompleted', (data) => {
      console.log('Game completed:', data);
      if (data) {
        setGameState('completed');
        
        if (data.dictionary) {
          setDictionary(data.dictionary);
        }
      }
    });
  }, [setGame, setGameState, setError, setActivePlayer, setActivePlayerName, setIsMyTurn, 
      setCurrentEra, setDictionary, setAssignedWords, setWordInputs, setAssignedWord, 
      setCurrentWord, setWordDescription, setGuesses, activePlayer]);

  // Socket event handlers
  const handleDictionaryUpdated = useCallback((data) => {
    console.log('Dictionary updated:', data);
    
    // Ensure dictionary is set correctly
    if (data.dictionary) {
      setDictionary(data.dictionary);
    }
    
    // Update player information if provided
    if (data.players && socket) {
      // Find the current player's data
      const currentPlayer = data.players.find(p => p.id === socket.id);
      
      // Update assigned words for the current player
      if (currentPlayer && Array.isArray(currentPlayer.assignedWords)) {
        console.log('Updating assigned words from dictionaryUpdated:', currentPlayer.assignedWords);
        setAssignedWords(currentPlayer.assignedWords);
      }
      
      // Update the game state with all players
      setGame(prevGame => {
        if (!prevGame) return null;
        return {
          ...prevGame,
          players: data.players
        };
      });
    }
    
    // Update game state if provided
    if (data.gameState) {
      console.log('Game state updated to:', data.gameState);
      setGameState(data.gameState);
    }
  }, [socket, setDictionary, setAssignedWords, setGameState, setGame]);

  const handleNewEraStarted = useCallback((data) => {
    console.log('New era started:', data);
    setCurrentEra(data.era);
    setActivePlayer(data.activePlayer);
    
    // Ensure active player name is a string
    if (data.activePlayer) {
      setActivePlayerName(ensureStringName(data.activePlayerName));
    }
    
    // Update game state
    if (data.state) {
      console.log('Game state updated to:', data.state);
      setGameState(data.state);
    }
  }, []);

  const handleGameCompleted = useCallback((data) => {
    console.log('Game completed:', data);
    
    // Update dictionary with final words
    if (data.dictionary) {
      setDictionary(data.dictionary);
    }
    
    // Set game state to completed
    if (data.gameState) {
      console.log('Game state updated to:', data.gameState);
      setGameState(data.gameState);
      
      // Update game object
      setGame(prevGame => ({
        ...prevGame,
        state: data.gameState
      }));
    } else {
      setGameState('completed');
      
      // Update game object
      setGame(prevGame => ({
        ...prevGame,
        state: 'completed'
      }));
    }
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
    setTimeout(() => setError(''), 3000);
  }, []);

  // Initialize socket connection
  const initializeSocket = useCallback((playerName, gameId = null) => {
    try {
      // Try to detect if we're on a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      console.log('Device detection for socket connection:', { isMobile });
  
      // ALWAYS use the Cloudflare tunnel URL - never use localhost
      const serverUrl = 'https://gibberers-server.16022001.xyz';
      console.log('Connecting to server at:', serverUrl);
  
      // Special settings for Cloudflare tunnel
      const socketOptions = {
        reconnection: true,
        reconnectionAttempts: isMobile ? 20 : 10,
        timeout: isMobile ? 60000 : 30000,
        reconnectionDelay: isMobile ? 2000 : 1000,
        reconnectionDelayMax: isMobile ? 10000 : 5000,
        // For Cloudflare tunnels, always use polling first on mobile
        transports: isMobile ? ['polling', 'websocket'] : ['websocket', 'polling'],
        forceNew: true,
        // Add query parameters for player identification
        query: {
          playerName: playerName
        }
      };
      
      console.log('Socket connection options:', socketOptions);
      
      // Create socket connection
      const socket = io(serverUrl, socketOptions);
      setSocket(socket);

      // Set up event listeners
      socket.on('connect', () => {
        console.log('Socket connected:', socket.id);
        setIsConnected(true);
        setError('');

        // Join or create game
        if (gameId) {
          console.log('Joining game:', gameId);
          socket.emit('joinGame', { gameId, playerName });
        } else {
          console.log('Creating new game');
          socket.emit('createGame', { playerName });
        }
      });

      socket.on('connect_error', (err) => {
        console.error('Socket connection error:', err);
        setError(`Connection error: ${err.message}`);
      });

      socket.on('connect_timeout', () => {
        console.error('Socket connection timeout');
        setError('Connection timeout - server may be down');
      });

      socket.on('error', (errorMsg) => {
        console.error('Socket error:', errorMsg);
        setError(`Game error: ${errorMsg}`);
      });

      socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        setIsConnected(false);
        if (reason === 'io server disconnect') {
          setError('Server disconnected - please refresh the page');
        } else if (reason === 'transport close') {
          setError('Connection lost - trying to reconnect...');
        }
      });

      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`Reconnection attempt ${attemptNumber}`);
        if (attemptNumber > 3) {
          setError(`Reconnection attempt ${attemptNumber} - server may be down`);
        }
      });

      socket.on('reconnect_failed', () => {
        console.error('Failed to reconnect');
        setError('Failed to reconnect - please refresh the page');
      });

      // Game event handlers
      setupGameEventHandlers(socket);

    } catch (error) {
      console.error('Error initializing socket:', error);
      setError('Failed to connect to the server. Please try again later.');
    }
  }, [setupGameEventHandlers]);

  // Game action functions
  const handleStartGame = useCallback(() => {
    if (socket && game && game.id) {
      socket.emit('startGame', { gameId: game.id });
    }
  }, [socket, game]);

  const handleWordInputChange = useCallback((word, value) => {
    setWordInputs(prev => ({
      ...prev,
      [word]: value
    }));
  }, []);

  const createWord = useCallback((word, spelling) => {
    console.log(`Creating word: ${word} with spelling: ${spelling} for game: ${game?.id}`);
    if (!game || !game.id) {
      console.error('Cannot create word: game ID is missing');
      setError('Game ID is missing. Please try again.');
      return;
    }
    
    // Different validation based on the era
    if (currentEra === 0) {
      // In Era 0, check if the word is in the assigned words
      if (!assignedWords.includes(word)) {
        console.error(`Word ${word} not in assigned words`);
        setError('This word is not in your assigned words. Please refresh the page.');
        return;
      }
    } else {
      // In Era 1+, check if it's the player's turn and if the word matches the current word
      if (socket.id !== activePlayer) {
        console.error('Not your turn to create a word');
        setError('Not your turn to create a word.');
        return;
      }
      
      if (gameState !== 'word_creation') {
        console.error('Game not in word creation state');
        setError('Game not in word creation state.');
        return;
      }
      
      if (word !== currentWord) {
        console.error(`Need to create a spelling for "${currentWord}", not "${word}"`);
        setError(`You need to create a spelling for "${currentWord}".`);
        return;
      }
    }
    
    // Check if the word is already in the dictionary with the same spelling
    const existingWord = dictionary.find(
      entry => entry.word.toLowerCase() === word.toLowerCase() && 
               entry.spelling.toLowerCase() === spelling.toLowerCase()
    );
    
    if (existingWord) {
      console.error(`Word ${word} with spelling ${spelling} already exists in dictionary`);
      setError('This word with this spelling already exists in the dictionary.');
      return;
    }
    
    // Emit the createWord event
    socket.emit('createWord', {
      gameId: game.id,
      word,
      spelling
    });
    
    // Immediately remove the word from assignedWords to update UI
    setAssignedWords(prev => prev.filter(w => w !== word));
    
    // Clear the input for this word
    setWordInputs(prev => ({
      ...prev,
      [word]: ''
    }));
    
    // Clear any error message
    setError('');
  }, [game, currentEra, assignedWords, socket, activePlayer, gameState, currentWord, dictionary]);

  const handleKeyPress = useCallback((e, word) => {
    if (e.key === 'Enter' && wordInputs[word]?.trim()) {
      createWord(word, wordInputs[word].trim());
    }
  }, [createWord, wordInputs]);

  const toggleWordVisibility = useCallback((word) => {
    setWordVisibility(prev => ({
      ...prev,
      [word]: !prev[word]
    }));
  }, []);

  const copyGameId = useCallback(() => {
    if (game && game.id) {
      navigator.clipboard.writeText(game.id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [game]);

  const handleReadyForNextEra = useCallback(() => {
    if (socket && game && game.id) {
      console.log('Marking player as ready for next era');
      socket.emit('readyForNextEra', { gameId: game.id });
      setReadyForNextEra(true);
    }
  }, [socket, game]);

  const handleStartTurn = useCallback(() => {
    if (socket && game && game.id && isMyTurn) {
      socket.emit('startTurn', { gameId: game.id });
    }
  }, [socket, game, isMyTurn]);

  const handleSubmitDescription = useCallback(() => {
    if (!descriptionInput.trim()) {
      setValidationError('Please enter a description');
      return;
    }

    // Validate that the description only uses words from the dictionary
    const words = descriptionInput.toLowerCase().split(/\s+/);
    const dictionaryWords = new Set();
    
    if (Array.isArray(dictionary)) {
      dictionary.forEach(entry => {
        dictionaryWords.add(entry.word.toLowerCase());
        dictionaryWords.add(entry.spelling.toLowerCase());
      });
    } else {
      // Handle old dictionary format (object)
      Object.keys(dictionary).forEach(key => {
        dictionaryWords.add(key.toLowerCase());
        if (dictionary[key].newWord) {
          dictionaryWords.add(dictionary[key].newWord.toLowerCase());
        }
      });
    }

    const invalidWords = words.filter(word => !dictionaryWords.has(word));
    if (invalidWords.length > 0) {
      setValidationError(`These words are not in the dictionary: ${invalidWords.join(', ')}`);
      return;
    }

    setValidationError('');
    if (socket && game && game.id) {
      console.log(`Submitting description for game: ${game.id}`);
      socket.emit('submitDescription', { 
        gameId: game.id, 
        description: descriptionInput.trim() 
      });
      setDescriptionInput('');
    } else {
      console.error('Cannot submit description: game ID is missing');
      setError('Game ID is missing. Please try again.');
    }
  }, [descriptionInput, dictionary, socket, game]);

  const handleSubmitGuess = useCallback(() => {
    if (!guessInput.trim()) {
      setValidationError('Please enter a guess');
      return;
    }

    setValidationError('');
    if (socket && game && game.id) {
      console.log(`Submitting guess for game: ${game.id}`);
      socket.emit('submitGuess', { 
        gameId: game.id, 
        guess: guessInput.trim() 
      });
      setGuessInput('');
    } else {
      console.error('Cannot submit guess: game ID is missing');
      setError('Game ID is missing. Please try again.');
    }
  }, [guessInput, socket, game]);

  const handleCompleteTurn = useCallback(() => {
    if (socket && game && game.id) {
      console.log(`Completing turn for game: ${game.id}`);
      socket.emit('completeTurn', { gameId: game.id });
    } else {
      console.error('Cannot complete turn: game ID is missing');
      setError('Game ID is missing. Please try again.');
    }
  }, [socket, game]);

  const toggleDebugInfo = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);

  const skipToEra1 = useCallback(() => {
    console.log('Skipping to Era 1');
    if (socket && game) {
      console.log(`Sending skipToEra1 request for game ${game.id}`);
      socket.emit('skipToEra1', { gameId: game.id });
    } else {
      console.error('Cannot skip to Era 1: socket or game not available', { socket: !!socket, game: !!game });
    }
  }, [socket, game]);

  // Clean up socket connection on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        console.log('Cleaning up socket connection...');
        // Remove all listeners before disconnecting to prevent memory leaks
        socket.removeAllListeners();
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [socket]);

  // Update isMyTurn when activePlayer changes
  useEffect(() => {
    if (socket && activePlayer) {
      setIsMyTurn(activePlayer === socket.id);
    }
  }, [activePlayer, socket]);

  // Provide all state and functions to children
  const value = {
    // Socket and connection state
    socket,
    isConnected,
    initializeSocket,
    
    // Game state
    game,
    setGame,
    error,
    setError,
    copySuccess,
    
    // Game-specific state
    dictionary,
    assignedWords,
    wordInputs,
    dictionaryLanguage,
    setDictionaryLanguage,
    wordVisibility,
    
    // Turn-based gameplay state
    currentEra,
    activePlayer,
    activePlayerName,
    isMyTurn,
    gameState,
    assignedWord,
    wordDescription,
    descriptionInput,
    setDescriptionInput,
    guessInput,
    setGuessInput,
    guesses,
    validationError,
    readyForNextEra,
    showDebug,
    currentWord,
    
    // Game actions
    handleStartGame,
    handleWordInputChange,
    createWord,
    handleKeyPress,
    toggleWordVisibility,
    copyGameId,
    handleReadyForNextEra,
    handleStartTurn,
    handleSubmitDescription,
    handleSubmitGuess,
    handleCompleteTurn,
    toggleDebugInfo,
    skipToEra1
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}; 